import { type FC, type RefObject, useCallback, useId } from "react";
import { Slot, Templated } from "../../templating";
import { useCore } from "../../contexts/useCore";
import { useCoreState } from "../../contexts/useCoreState";
import { useScroll } from "../../contexts/useScroll";
import { useScrollAnchorOnResize } from "../../contexts/useScrollAnchorOnResize";
import { useReadingAnchor } from "../ConversationPage/useReadingAnchor";
import { useTranslation } from "../../contexts/useTranslation";
import { useWidget } from "../../contexts/useWidget";
import { useSupportAgents } from "../../contexts/useSupportAgents";
import Navbar from "../../components/Navbar";
import Avatar from "../../components/Avatar";
import ChatMessageList from "../ConversationPage/components/ChatMessageList";
import ChatFormText from "../ConversationPage/components/ChatFormText";
import ConversationStarterButton from "../LandingPage/components/ConversationStarterButton";
import {
  getResponseTimeTranslation,
  getSupportAgentNameTranslation,
} from "../../utils/translationUtil";
import { sortUsersByAvatar } from "../../utils/userUtil";
import BrandAvatar from "../../components/BrandAvatar";
import type { LastBotState } from "../../core/state";
import type { Message } from "../../core/types";
import type { PageOptions } from "../../types/page";

const threadSelector = (state: LastBotState): LastBotState["thread"] => state.thread;
const EMPTY_MESSAGES: Message[] = [];

interface CustomMainPageProps {
  containerRef: RefObject<HTMLDivElement>;
  navigate: (_pageOptions: PageOptions) => void;
  isFullscreen: boolean;
}

/**
 * Single-page surface activated by the host-page `<template id="lastbot-custom-main-page">`.
 *
 * Two slots:
 *   - `welcome`: embedder chrome (defaults to the LandingPage welcome — logo, welcome text,
 *     conversation starters, response time, agent presence). Conversation-starter clicks
 *     call `core.startNewThread(description)` directly — no internal navigation.
 *   - `chat`: widget-rendered conversation surface (message list when a thread is active +
 *     composer). The composer (`ChatFormText`) calls `core.sendMessage(text)` on submit.
 *
 * Other system pages (login, enter-code, chat-history, error) still render normally when
 * triggered (e.g. by the navbar history button or a server 422). The `navigate` callback
 * passed in handles those transitions.
 */
const CustomMainPage: FC<CustomMainPageProps> = ({
  containerRef,
  navigate,
  isFullscreen,
}) => {
  const { translate } = useTranslation();
  const { conversationStarters, averageResponseTime } = useWidget();
  const { supportAgents } = useSupportAgents();
  const core = useCore();
  const thread = useCoreState(core, threadSelector);
  const { scrollRef, restoreReadingPosition, hasReadingAnchor } = useScroll();
  const id = useId();

  const agentsToDisplay = sortUsersByAvatar(supportAgents).slice(0, 3);

  // Scroll on new messages (matches ConversationPage behavior): the reader's
  // question to the top when a reply starts, the bottom otherwise.
  useReadingAnchor(thread?.messages ?? EMPTY_MESSAGES, thread !== null, thread?.id ?? null);

  // Keep the latest answer visible when rich-media sizing resizes the panel
  // (LAS-2072) — the same anchor ConversationPage installs.
  useScrollAnchorOnResize(core, scrollRef, restoreReadingPosition, hasReadingAnchor);

  const handleStarterClick = useCallback(
    (description: string): void => {
      void core.startNewThread(description).catch((err: unknown) => {
        const status = (err as { cause?: { status?: number } })?.cause?.status;
        if (status !== 499) {
          navigate({ page: { type: "errorPage" } });
        }
      });
    },
    [core, navigate],
  );

  // Voice mode in single-page is out of scope for v1. The ChatFormText composer still
  // renders its voice button when `voiceModeAvailable` is true; clicking it is a no-op
  // here. Embedders who need voice mode should use the multi-page flow.
  const handleStartVoiceMode = useCallback((): void => {
    /* no-op */
  }, []);

  return (
    <Templated id="lastbot-custom-main-page">
      <>
        <Slot name="welcome">
          {/*
            Welcome region is composed from named sub-slots so an embedder
            can either:
              (a) project the whole region via `<slot name="welcome">` in
                  their template (default — gets navbar + avatar + text +
                  starters + presence), OR
              (b) project the sub-slots individually
                  (`<slot name="brand-avatar">`, `<slot name="welcome-text">`,
                  `<slot name="conversation-starters">`,
                  `<slot name="agent-presence">`) and omit any they don't
                  want. The omitted sub-slots are harvested but never
                  projected, so they don't render.
            Same pattern as `LandingPage.tsx`.
          */}
          <>
            <Slot name="navbar">
              <Navbar isFullscreen={isFullscreen} />
            </Slot>
            <div className="flex flex-col gap-y-3 items-center justify-center text-center px-4 pt-4">
              <Slot name="brand-avatar">
                <BrandAvatar />
              </Slot>
              <Slot name="welcome-text">
                <>
                  <div className="text-3xl tracking-wide text-lastbot-on-surface">
                    {translate("how_can_we_help")}
                  </div>
                  <div className="text-xs/6 text-lastbot-icon-muted">
                    {translate("our_support_team_is_here_to_help_you")}
                  </div>
                </>
              </Slot>
              {conversationStarters.length > 0 && thread === null && (
                <Slot name="conversation-starters">
                  <div className="flex gap-x-1 justify-center pt-2">
                    {conversationStarters.map(([title, description], i) => (
                      <ConversationStarterButton
                        key={`${id}-${i}`}
                        containerRef={containerRef}
                        title={title}
                        description={description}
                        onClick={() => handleStarterClick(description)}
                      />
                    ))}
                  </div>
                </Slot>
              )}
              <Slot name="agent-presence">
                <div className="flex items-center justify-center gap-x-2 pt-2">
                  {agentsToDisplay.length > 0 && (
                    <div className="flex">
                      {agentsToDisplay.map((agent, i) => (
                        <Avatar
                          key={agent.id}
                          url={agent.avatar_url}
                          name={getSupportAgentNameTranslation(translate, agent)}
                          className={`${i > 0 ? "-ml-1.5" : ""}`}
                        />
                      ))}
                    </div>
                  )}
                  <div className="text-xs/6 text-lastbot-icon-muted">
                    {getResponseTimeTranslation(translate, averageResponseTime)}
                  </div>
                </div>
              </Slot>
            </div>
          </>
        </Slot>
        <Slot name="chat">
          {/*
            Like the welcome region, the chat region is composed from named
            sub-slots so embedders can either project the whole thing via
            `<slot name="chat">` or pick out `<slot name="message-list">` /
            `<slot name="composer-text">` individually. Matches the slot
            layout `ConversationPage` already exposes.
          */}
          <>
            {thread !== null && (
              <div
                ref={scrollRef}
                className="flex-1 flex flex-col p-4 overflow-y-auto"
                data-testid="custom-main-messages"
              >
                <Slot name="message-list">
                  <ChatMessageList
                    messageThreadId={thread.id.toString()}
                    messages={thread.messages}
                    threadClosedAt={thread.closedAt}
                    rating={thread.rating}
                  />
                </Slot>
              </div>
            )}
            <Slot name="composer-text">
              <ChatFormText
                // When no thread exists yet, core.sendMessage() creates one
                // internally on submit (see LastBotCore.sendMessage), so the
                // composer should be sendable from the start. Once a thread
                // exists, gate on its channel-connected state the same way
                // ConversationPage does.
                connected={thread === null || thread.connected}
                onStartVoiceMode={handleStartVoiceMode}
              />
            </Slot>
          </>
        </Slot>
      </>
    </Templated>
  );
};

export default CustomMainPage;
